<script setup lang="ts">
  import { computed, onBeforeMount, onUnmounted, ref, watch } from 'vue'
  import {
    useRoute,
    useRouter,
    type RouteLocationNormalizedLoaded,
    type RouteMeta,
    type RouteRecordNormalized,
  } from 'vue-router'

  interface Crumb {
    label: string
    name?: string | undefined
    navigable: boolean
  }

  const route = useRoute()
  const router = useRouter()
  const dynamicTitle = ref<string | null>(null)
  const dynamicTitleRoute = ref<string | null>(null)

  const routesByName = computed(() => {
    const map = new Map<string, RouteRecordNormalized>()
    for (const route of router.getRoutes()) {
      if (route.name) map.set(String(route.name), route)
    }
    return map
  })

  function hasComponent(route: RouteRecordNormalized) {
    return Boolean(route.components?.['default'])
  }

  const crumbs = computed<Crumb[]>(() => {
    const map = routesByName.value
    const result: Crumb[] = []
    const seen = new Set<string>()

    let current = route.name ? map.get(String(route.name)) : undefined

    while (current) {
      const key = String(current.name)
      if (seen.has(key)) break
      seen.add(key)

      const label = getTitleForRoute(current, route)

      if (label) {
        result.unshift({
          label,
          name: current.name ? String(current.name) : undefined,
          navigable: hasComponent(current),
        })
      }

      const parentName = resolveBreadcrumbParent(current.meta, route)
      current = parentName ? map.get(parentName) : undefined
    }

    return result
  })

  function resolveBreadcrumbParent(meta: RouteMeta, route: RouteLocationNormalizedLoaded) {
    const raw = meta.breadcrumbParent
    return typeof raw === 'function' ? raw(route) : raw
  }

  const staticPrefix = computed(() => {
    const end = crumbs.value.findIndex(crumb => crumb.navigable)
    return end === -1 ? crumbs.value : crumbs.value.slice(0, end)
  })

  const trail = computed(() => {
    const end = crumbs.value.findIndex(crumb => crumb.navigable)
    return end === -1 ? [] : crumbs.value.slice(end)
  })

  function isClickable(crumb: Crumb, index: number) {
    return crumb.navigable && index !== trail.value.length - 1
  }

  function navigate(crumb: Crumb, index: number) {
    if (!isClickable(crumb, index) || !crumb.name) return
    router.push({ name: crumb.name, params: route.params })
  }

  function resolveTitle(meta: RouteMeta, route: RouteLocationNormalizedLoaded) {
    const raw = meta.title
    return typeof raw === 'function' ? raw(route) : raw
  }

  function setTitle(event: CustomEvent) {
    const customEvent = event
    dynamicTitle.value = customEvent.detail.title
    dynamicTitleRoute.value = route.fullPath
  }

  function getTitleForRoute(
    current: RouteRecordNormalized,
    currentRoute: RouteLocationNormalizedLoaded
  ) {
    const isCurrentPage = current.name === currentRoute.name
    if (isCurrentPage && current.meta.dynamicTitle) {
      return dynamicTitle.value ?? ''
    }
    return resolveTitle(current.meta, currentRoute)
  }

  onBeforeMount(() => {
    window.addEventListener('setPageTitle', setTitle)
  })

  onUnmounted(() => {
    window.removeEventListener('setPageTitle', setTitle)
  })

  watch(
    () => route.fullPath,
    () => {
      dynamicTitle.value = null
      dynamicTitleRoute.value = route.fullPath
    },
    { immediate: true }
  )
</script>

<template>
  <nav
    v-if="staticPrefix.length > 0 || trail.length > 0"
    class="breadcrumb-nav"
    aria-label="breadcrumb"
  >
    <span v-for="prefix in staticPrefix" :key="prefix.label" class="breadcrumb-prefix">
      {{ prefix.label }}
    </span>

    <ol v-if="trail.length > 0" class="breadcrumb mb-0">
      <li
        v-for="(crumb, index) in trail"
        :key="crumb.name ?? crumb.label"
        class="breadcrumb-item"
        :class="{ active: index === trail.length - 1, clickable: isClickable(crumb, index) }"
        :role="isClickable(crumb, index) ? 'link' : undefined"
        :tabindex="isClickable(crumb, index) ? 0 : undefined"
        @click="navigate(crumb, index)"
        @keydown.enter="navigate(crumb, index)"
      >
        {{ crumb.label }}
      </li>
    </ol>
  </nav>
</template>

<style scoped lang="css">
  .breadcrumb-nav {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    overflow: hidden;
  }

  .breadcrumb-prefix {
    font-size: 1.1rem;
    font-weight: bold;
    color: var(--colours-grey-1);
  }

  .breadcrumb-prefix::after {
    content: '-';
    padding-left: var(--spacing-xs);
    font-weight: bold;
    color: var(--colours-grey-1);
    opacity: 0.7;
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    list-style: none;
    font-size: 1rem;
  }

  .breadcrumb-item {
    display: flex;
    align-items: center;
    opacity: 0.55;
  }

  .breadcrumb-item + .breadcrumb-item::before {
    content: '-';
    color: var(--colours-grey-1);
    opacity: 0.7;
  }

  .breadcrumb-item.clickable {
    color: var(--colours-grey-2);
    cursor: pointer;
  }

  .breadcrumb-item.clickable:hover {
    text-decoration: underline;
  }

  .breadcrumb-item.active {
    opacity: 1;
    font-weight: bold;
    font-size: 1.1rem;
    color: var(--colours-grey-2);
    cursor: default;
    background: none;
  }
</style>
